/* eslint-disable no-unused-vars */
import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { documentsCompleteUrl, documentsUrl } from 'constants/urls';

import axiosClient from './axios-client';
import { getQueryToken } from './helpers';

export type ICreateDocumentRequestBody = {
  documentTypeName: string;
  documentTypeReference: string;
  filename: string;
  fileSize: number;
  dateTag?: string;
};

const createDocument: (
  doc: ICreateDocumentRequestBody,
  file: File,
  config?: AxiosRequestConfig & {
    onUploadProgress: (
      progressEvent: ProgressEvent,
      totalParts: number,
      key: number
    ) => void;
  },
  onSuccess?: (res: unknown) => void,
  onError?: (error: unknown) => void
) => Promise<void> = async (data, file, config, onSuccess, onError) => {
  try {
    const { data: signedUrlsResponse } = await axiosClient.post(
      documentsUrl,
      data,
      {
        headers: { token: getQueryToken() as string },
        cancelToken: config?.cancelToken,
      }
    );

    const partsUploadPromises = Object.entries(signedUrlsResponse.urls).map<
      Promise<[string, AxiosResponse<any, any>]>
    >(([key, signedUrl], _, array) => {
      const index = parseInt(key, 10); // Note index start 1 (instead of 0)

      // Split file
      const start = (index - 1) * signedUrlsResponse.partSize;
      const end = index * signedUrlsResponse.partSize;
      const blob =
        index < array.length ? file.slice(start, end) : file.slice(start);

      return axios
        .put(signedUrl as string, blob, {
          ...config,
          onUploadProgress: (progressEvent) =>
            config?.onUploadProgress?.(
              progressEvent,
              signedUrlsResponse.numberOfParts,
              index
            ),
        })
        .then((res) => [key, res]);
    });

    // Upload al part of file
    const uploadResponses = await Promise.all(partsUploadPromises);

    const completionRequestBody: {
      objKey: string;
      uploadId: string;
      eTagByPartNumbers: Record<string, string>;
    } = {
      objKey: signedUrlsResponse.objKey,
      uploadId: signedUrlsResponse.uploadId,
      eTagByPartNumbers: {},
    };

    uploadResponses.forEach(([key, response]) => {
      const unquotedEtag = response.headers.etag.replace(/['"]+/g, '');
      completionRequestBody.eTagByPartNumbers[key] = unquotedEtag;
    });

    const { data: finalResponse } = await axiosClient.post(
      documentsCompleteUrl,
      completionRequestBody,
      {
        headers: { token: getQueryToken() as string },
        cancelToken: config?.cancelToken,
      }
    );

    onSuccess?.(finalResponse);
  } catch (error) {
    onError?.(error);
  }
};

export default createDocument;
